import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, inboxApi, isLastAdministrator, useAuth, type Role, type TeamMember } from '@wa/shared';

/**
 * The people, their roles, who they report to, and which numbers they work.
 *
 * The row carries four different decisions, and at 360px that is more than fits on
 * one line. The identity keeps a real flex basis so it is never the thing that
 * shrinks; everything else wraps beneath it. A name rendered as "Rak…" is worse
 * than a control on a second line.
 */
export function PeoplePanel() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const team = useQuery({ queryKey: ['team'], queryFn: inboxApi.team });
  const roles = useQuery({ queryKey: ['roles'], queryFn: inboxApi.roles, staleTime: 60_000 });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });

  const refresh = () => {
    for (const key of ['team', 'agents', 'agent-groups', 'roles']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const create = useMutation({
    mutationFn: inboxApi.createTeamMember,
    onSuccess: (_data, variables) => {
      setError(null);
      // Shown once, because it is never retrievable again: the server keeps a
      // bcrypt hash and returns nothing.
      setIssued({ email: variables.email, password: variables.password });
      refresh();
    },
    onError: (e) => fail(e, 'Could not add that person.'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof inboxApi.updateTeamMember>[1] }) =>
      inboxApi.updateTeamMember(id, patch),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => fail(e, 'Could not change that account.'),
  });

  const setChannels = useMutation({
    mutationFn: ({ id, channelIds }: { id: number; channelIds: number[] }) =>
      inboxApi.setTeamChannels(id, channelIds),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => fail(e, 'Could not change which numbers they work.'),
  });

  const away = useMutation({
    mutationFn: inboxApi.forceAway,
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not mark them away.'),
  });

  const reset = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => inboxApi.resetTeamPassword(id, password),
    onSuccess: (_data, variables) => {
      setError(null);
      const member = (team.data ?? []).find((m) => m.id === variables.id);
      if (member) setIssued({ email: member.email, password: variables.password });
      refresh();
    },
    onError: (e) => fail(e, 'Could not reset that password.'),
  });

  const members = team.data ?? [];
  const roleList = roles.data ?? [];
  const channelList = channels.data ?? [];
  const busy = update.isPending || reset.isPending || away.isPending || setChannels.isPending;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {issued && <IssuedPassword issued={issued} onDismiss={() => setIssued(null)} />}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          {members.length} {members.length === 1 ? 'person' : 'people'}
        </h2>

        <ul className="divide-y divide-(--color-line-dim)">
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              team={members}
              roles={roleList}
              channels={channelList}
              isMe={member.id === user?.id}
              isLastAdmin={isLastAdministrator(members, member, roleList)}
              isBusy={busy}
              isOpen={expanded === member.id}
              onToggle={() => setExpanded((v) => (v === member.id ? null : member.id))}
              onPatch={(patch) => update.mutate({ id: member.id, patch })}
              onChannels={(channelIds) => setChannels.mutate({ id: member.id, channelIds })}
              onAway={() => away.mutate(member.id)}
              onReset={(password) => reset.mutate({ id: member.id, password })}
            />
          ))}
        </ul>

        <AddMember roles={roleList} isBusy={create.isPending} onCreate={(m) => create.mutate(m)} />
      </section>
    </div>
  );
}

/**
 * The one moment a password is visible. Dismissed only by the button: an
 * administrator who navigates away without copying it has to issue another one.
 */
function IssuedPassword({ issued, onDismiss }: {
  issued: { email: string; password: string }; onDismiss: () => void;
}) {
  return (
    <div role="status" className="rounded-lg border border-(--color-warn) bg-(--color-note) px-3 py-3 sm:px-4">
      <p className="text-small font-semibold text-(--color-note-strong)">
        Copy this now &mdash; it cannot be shown again
      </p>
      <p className="mt-1 text-small text-(--color-note-ink)">
        Send it to <b className="break-all">{issued.email}</b> yourself. There is no email
        delivery yet, and the server keeps only a hash.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded border border-(--color-note-line) bg-(--color-surface) px-2 py-1.5 font-mono text-small">
          {issued.password}
        </code>
        <button
          type="button"
          onClick={onDismiss}
          className="tap shrink-0 rounded bg-(--color-note-strong) px-3 py-1.5 text-small font-medium text-white"
        >
          I have copied it
        </button>
      </div>
    </div>
  );
}

interface MemberRowProps {
  member: TeamMember;
  team: TeamMember[];
  roles: Role[];
  channels: { id: number; displayPhoneNumber: string | null; verifiedName: string | null; label?: string | null }[];
  isMe: boolean;
  isLastAdmin: boolean;
  isBusy: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onPatch: (patch: { roleId?: number; isActive?: boolean; supervisorId?: number; clearSupervisor?: boolean }) => void;
  onChannels: (channelIds: number[]) => void;
  onAway: () => void;
  onReset: (password: string) => void;
}

function MemberRow(props: MemberRowProps) {
  const { member, team, roles, channels, isMe, isLastAdmin, isBusy, isOpen } = props;
  const [password, setPassword] = useState('');

  // Mirrors the server's guards so a control can be greyed with a reason rather
  // than offered and then refused. The server check is still the real one.
  const roleLocked = isMe ? 'You cannot change your own role.'
    : isLastAdmin ? 'The only person who can manage the team. Give somebody else that permission first.'
    : !member.isActive ? 'Reactivate them before changing their role.'
    : null;

  const activeLocked = isMe ? 'You cannot deactivate your own account.'
    : isLastAdmin ? 'The only person who can manage the team. Give somebody else that permission first.'
    : null;

  const restricted = (member.channelIds ?? '').split(',').filter(Boolean).map(Number);

  return (
    <li className={['px-3 py-2.5 sm:px-4', member.isActive ? '' : 'bg-(--color-sunken)'].join(' ')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* A real flex basis, not min-w-0: with min-w-0 the identity shrinks to
            nothing and the controls take the whole row, which at 360px rendered
            people as "P." and "Rak…". */}
        {/* The name opens the same panel as More: nobody guesses that a person's
            numbers and supervisor sit behind a button on the far right. */}
        <button
          type="button"
          onClick={props.onToggle}
          aria-expanded={isOpen}
          className="tap min-w-0 flex-1 basis-48 rounded text-left hover:bg-(--color-sunken)"
        >
          <p className="flex items-center gap-1.5 truncate text-small font-medium">
            {member.isActive && (
              <span
                aria-hidden="true"
                className={['size-2 shrink-0 rounded-full',
                  member.availability === 1 ? 'bg-(--color-good)' : 'bg-(--color-faint)'].join(' ')}
              />
            )}
            <span className="truncate">{member.displayName}</span>
            {isMe && <span className="shrink-0 text-micro font-normal text-(--color-faint)">(you)</span>}
          </p>
          <p className="truncate text-small text-(--color-muted)">
            {member.email}
            {member.isActive
              ? <> &middot; {member.availability === 1 ? 'Available' : 'Away'} &middot; {member.openConversations} open</>
              : <> &middot; deactivated</>}
          </p>
        </button>

        <div className="flex shrink-0 flex-wrap items-center gap-1">
          <select
            value={member.roleId ?? ''}
            disabled={isBusy || roleLocked !== null}
            aria-label={`Role for ${member.displayName}`}
            title={roleLocked ?? 'What this person is allowed to do.'}
            onChange={(e) => props.onPatch({ roleId: Number(e.target.value) })}
            className="rounded border border-(--color-line) bg-(--color-surface) px-1.5 py-1 text-small disabled:opacity-50"
          >
            {/* Only when their role has been deleted out from under them. They can
                sign in and every route refuses them, so it must be visible. */}
            {member.roleId === null && <option value="">No role</option>}
            {roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
          </select>

          <button
            type="button"
            onClick={props.onToggle}
            aria-expanded={isOpen}
            className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken)"
          >
            {isOpen ? 'Less' : 'More'}
          </button>

          <button
            type="button"
            disabled={isBusy || activeLocked !== null}
            title={activeLocked ?? undefined}
            onClick={() => props.onPatch({ isActive: !member.isActive })}
            className={[
              'tap rounded border px-2 py-1 text-small transition disabled:opacity-50',
              member.isActive
                ? 'border-(--color-line) text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad)'
                : 'border-(--color-brand) text-(--color-brand)',
            ].join(' ')}
          >
            {member.isActive ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-3 flex flex-col gap-3 rounded-(--radius-field) bg-(--color-sunken) p-3">
          <label className="flex flex-col gap-1">
            <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Reports to</span>
            <select
              value={member.supervisorId ?? ''}
              disabled={isBusy}
              onChange={(e) => props.onPatch(e.target.value
                ? { supervisorId: Number(e.target.value) }
                : { clearSupervisor: true })}
              className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            >
              <option value="">Nobody</option>
              {team.filter((m) => m.id !== member.id && m.isActive).map((m) => (
                <option key={m.id} value={m.id}>{m.displayName}</option>
              ))}
            </select>
            <span className="text-micro text-(--color-muted)">
              A supervisor can see every conversation assigned to anybody beneath them.
            </span>
          </label>

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">
              Numbers they work
            </legend>

            {channels.map((channel) => (
              <label key={channel.id} className="flex items-center gap-2 text-small">
                <input
                  type="checkbox"
                  disabled={isBusy}
                  checked={restricted.length === 0 || restricted.includes(channel.id)}
                  onChange={(e) => {
                    // Every box ticked is the same state as none: unrestricted. So
                    // ticking the last one clears the restriction rather than
                    // storing a row per number.
                    const next = e.target.checked
                      ? [...new Set([...(restricted.length === 0 ? channels.map((c) => c.id) : restricted), channel.id])]
                      : (restricted.length === 0 ? channels.map((c) => c.id) : restricted).filter((id) => id !== channel.id);

                    props.onChannels(next.length === channels.length ? [] : next);
                  }}
                />
                <span className="truncate">
                  {channel.label ? `${channel.label} · ` : ''}{channel.displayPhoneNumber ?? `#${channel.id}`}
                  {channel.verifiedName && <span className="text-(--color-muted)"> &middot; {channel.verifiedName}</span>}
                </span>
              </label>
            ))}

            <span className="text-micro text-(--color-muted)">
              {restricted.length === 0
                ? 'Every number. They see all conversations and can be assigned any of them.'
                : 'Only the ticked numbers. Everything else is hidden from their inbox.'}
            </span>
          </fieldset>

          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-40 flex-1 flex-col gap-1">
              <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">New password</span>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 12 characters"
                className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
              />
            </label>
            <button
              type="button"
              disabled={password.length < 12 || isBusy}
              onClick={() => { props.onReset(password); setPassword(''); }}
              className="tap shrink-0 rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              Set password
            </button>

            {member.isActive && member.availability === 1 && !isMe && (
              <button
                type="button"
                disabled={isBusy}
                onClick={props.onAway}
                title="Stop new conversations being assigned to them."
                className="tap shrink-0 rounded border border-(--color-line) px-2 py-1.5 text-small text-(--color-muted) hover:bg-(--color-surface)"
              >
                Mark away
              </button>
            )}

            <p className="w-full text-micro text-(--color-muted)">
              Setting a password signs them out everywhere, on every device.
            </p>
          </div>
        </div>
      )}
    </li>
  );
}

function AddMember({ roles, isBusy, onCreate }: {
  roles: Role[];
  isBusy: boolean;
  onCreate: (m: { displayName: string; email: string; password: string; roleId: number }) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState<number | ''>('');
  const [showProblems, setShowProblems] = useState(false);

  const chosen = roleId === '' ? roles.find((r) => r.name === 'Agent')?.id ?? roles[0]?.id : roleId;

  // The button used to sit disabled until every rule was met, and nothing on
  // the screen said which rule — a 10-character password left it grey for
  // good. Now it is always clickable; a click with something missing names
  // the missing thing next to its field instead.
  const problems = {
    name: displayName.trim().length === 0 ? 'Enter their name.' : null,
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'Enter a valid email address.' : null,
    password: password.length < MinPasswordLength
      ? `${MinPasswordLength - password.length} more character${MinPasswordLength - password.length === 1 ? '' : 's'} needed (${password.length} of ${MinPasswordLength}).`
      : null,
    role: chosen === undefined ? 'No roles are available yet — create one under Roles first.' : null,
  };
  const ready = !problems.name && !problems.email && !problems.password && !problems.role;

  const submit = () => {
    if (!ready) { setShowProblems(true); return; }
    onCreate({ displayName: displayName.trim(), email: email.trim(), password, roleId: chosen! });
    setDisplayName(''); setEmail(''); setPassword(''); setRoleId(''); setShowProblems(false);
  };

  const hint = (key: keyof typeof problems) => (showProblems && problems[key]
    ? <span className="mt-0.5 block text-micro text-(--color-bad)">{problems[key]}</span>
    : null);

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      className="flex flex-wrap items-end gap-2 border-t border-(--color-line) bg-(--color-sunken) px-3 py-3 sm:px-4"
    >
      <Field label="Name" className="min-w-36 flex-1">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Priya Sharma"
          aria-invalid={showProblems && !!problems.name}
          className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
        />
        {hint('name')}
      </Field>

      <Field label="Email" className="min-w-40 flex-1">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="priya@example.com"
          aria-invalid={showProblems && !!problems.email}
          className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
        />
        {hint('email')}
      </Field>

      <Field label={`Password (at least ${MinPasswordLength} characters)`} className="min-w-48 flex-1">
        <div className="flex gap-1">
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Type one or generate"
            autoComplete="off"
            aria-invalid={showProblems && !!problems.password}
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
          <button
            type="button"
            onClick={() => setPassword(generatePassword())}
            className="tap shrink-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          >
            Generate
          </button>
        </div>
        {hint('password')}
      </Field>

      <Field label="Role">
        <select
          value={chosen ?? ''}
          onChange={(e) => setRoleId(Number(e.target.value))}
          className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
        >
          {roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
        </select>
        {hint('role')}
      </Field>

      <button
        type="submit"
        disabled={isBusy}
        aria-disabled={!ready}
        className={`tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40 ${ready ? '' : 'opacity-70'}`}
      >
        {isBusy ? 'Adding…' : 'Add person'}
      </button>

      <p className="w-full text-micro text-(--color-muted)">
        They start as Away and will not receive conversations until they sign in and
        mark themselves Available.
      </p>
    </form>
  );
}

// Same rule as AgentEndpoints.MinPasswordLength; the server refuses shorter.
const MinPasswordLength = 12;

// 16 characters from a set without look-alikes (no 0/O, 1/l/I), from the
// browser's CSPRNG. The admin reads it out once, so it has to be typeable.
function generatePassword(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

function Field({ label, className, children }: {
  label: string; className?: string; children: React.ReactNode;
}) {
  return (
    <label className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</span>
      {children}
    </label>
  );
}
